import { useState } from 'react';
import clsx from 'clsx';
import type { Poll, PollOption } from '@/lib/api';
import { Polls } from '@/lib/api';
import { useChatStore } from '@/stores/chat';

interface Props {
  poll: Poll;
  canVote?: boolean;
}

export default function PollMessage({ poll: initialPoll, canVote = true }: Props) {
  const poll = useChatStore((s) => s.pollsById.get(initialPoll.id) ?? initialPoll);
  const upsertPoll = useChatStore((s) => s.upsertPoll);
  const [selected, setSelected] = useState<Set<string>>(new Set(poll.userVotes));
  const [submitting, setSubmitting] = useState(false);

  const totalVotes = poll.options.reduce((acc, o) => acc + o.voteCount, 0);
  const userHasVoted = poll.userVotes.length > 0;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (poll.allowMultiple) {
        if (next.has(id)) next.delete(id);
        else next.add(id);
      } else {
        next.clear();
        next.add(id);
      }
      return next;
    });
  }

  async function vote() {
    if (selected.size === 0 || submitting) return;
    setSubmitting(true);
    // Snapshot what was selected so we can roll back on failure — otherwise
    // the UI shows a "voted" highlight the server never recorded.
    const submittedSelection = new Set(selected);
    try {
      const optionIds = Array.from(submittedSelection);
      await Polls.vote(poll.id, optionIds);
      const fresh = await Polls.get(poll.id);
      upsertPoll({ ...poll, options: fresh.options, userVotes: fresh.userVotes });
      // Sync the local selection to whatever the server actually accepted.
      setSelected(new Set(fresh.userVotes));
    } catch (err) {
      const reason = err && typeof err === 'object' && 'message' in err
        ? String((err as { message: unknown }).message)
        : 'unknown error';
      // Best-effort toast — we look up a global toast function on window to
      // avoid threading a context provider through the PollMessage tree.
      const toast = (window as unknown as { __chatToast?: (m: string) => void }).__chatToast;
      if (toast) toast(`Couldn't submit vote: ${reason}`);
      setSelected(new Set(poll.userVotes));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-2">
      <div className="font-medium">{poll.question}</div>
      <ul className="space-y-1">
        {poll.options.map((o: PollOption) => {
          const isMine = selected.has(o.id) || poll.userVotes.includes(o.id);
          const pct = totalVotes > 0 ? Math.round((o.voteCount / totalVotes) * 100) : 0;
          return (
            <li key={o.id}>
              <button
                type="button"
                disabled={!canVote}
                onClick={() => toggle(o.id)}
                className={clsx(
                  'relative w-full overflow-hidden rounded-md border px-3 py-1.5 text-left text-sm transition',
                  isMine
                    ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-700/30'
                    : 'border-ink-200 hover:bg-ink-50 dark:border-ink-700 dark:hover:bg-ink-700/50',
                  !canVote && 'cursor-default',
                )}
                aria-pressed={isMine}
              >
                <span
                  aria-hidden="true"
                  className="absolute inset-y-0 left-0 bg-brand-200/60 dark:bg-brand-500/30"
                  style={{ width: `${pct}%` }}
                />
                <span className="relative flex items-center justify-between">
                  <span className="truncate">{o.text}</span>
                  <span className="ml-2 shrink-0 text-xs">
                    {o.voteCount} {totalVotes > 0 ? `(${pct}%)` : ''}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between text-xs">
        <span className="text-ink-500 dark:text-ink-300">{totalVotes} voted</span>
        {canVote && (
          <button
            type="button"
            className="btn-primary px-2 py-1 text-xs"
            onClick={vote}
            disabled={selected.size === 0 || submitting}
          >
            {submitting ? 'Voting…' : userHasVoted ? 'Update vote' : 'Vote'}
          </button>
        )}
      </div>
    </div>
  );
}
